///
/// EarthBrowser
///
/// Created by Matt Giger on 9/6/2026
/// Copyright (c) 2026 Geodata Labs
///

import { DesignColors } from "@/earthbrowser/design-system/DesignColors"
import { PolylineNode } from "@/earthbrowser/entities/Polyline/PolylineNode"
import { cSurfaceOverlayDepthBias } from "@/earthbrowser/entities/surfaceDepthBias"
import { FontCache } from "@/earthbrowser/utils/FontCache"
import { geographic } from "@/earthbrowser/utils/geographic"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { trig } from "@stovokor/math/math"
import { type vec3 } from "@stovokor/math/vector"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { buildFlatLabel, cStormLabelLiftKm } from "./StormForecastPointsNode"
import { destinationLatLon, type LandfallPoint } from "./stormTrack"

const cLabelFont = "/fonts/Inter-Bold.otf"

// A diamond (rotated square) distinguishes the landfall point from the
// circular forecast position markers
const cDiamondRadiusKm = 22
const cDiamondWidth = 2.5
const cLabelSouthOffsetKm = 55

/**
 * Landfall callout: a diamond marker where the forecast track first crosses
 * the coastline, labeled with the estimated arrival time in the viewer's
 * local time. Only shown while the storm is focused (the parent toggles
 * visibility) since it's a derived estimate, not advisory data.
 */
export class StormLandfallNode extends NodeBase {
	constructor(context: StovokorContext, landfall: LandfallPoint, altitudeKm: number) {
		super()

		const positions: vec3[] = []
		const angularRadius = cDiamondRadiusKm / 6371
		for (const azimuthDeg of [0, 90, 180, 270]) {
			const { lat, lon } = destinationLatLon(landfall.lat, landfall.lon, azimuthDeg * trig.rad, angularRadius)
			positions.push(new geographic(lon * trig.rad, lat * trig.rad, altitudeKm).cartesian)
		}
		this.addNode(new PolylineNode(context, {
			positions,
			width: cDiamondWidth,
			color: DesignColors.STORM_LANDFALL,
			loop: true,
			depthWrite: false,
			polygonOffset: cSurfaceOverlayDepthBias,
		}))

		void this.buildLabel(context, landfall, altitudeKm)
	}

	private async buildLabel(context: StovokorContext, landfall: LandfallPoint, altitudeKm: number): Promise<void> {
		const font = await FontCache.load(cLabelFont)
		const date = new Date(landfall.t)
		const weekday = date.toLocaleDateString(undefined, { weekday: "short" })
		const hour = date.toLocaleTimeString(undefined, { hour: "numeric" })
		this.addNode(buildFlatLabel(
			context,
			font,
			`Landfall ~${weekday} ${hour}`,
			DesignColors.STORM_LANDFALL,
			landfall.lat,
			landfall.lon,
			altitudeKm + cStormLabelLiftKm,
			cLabelSouthOffsetKm
		))
	}
}
